import { useState } from "react";
import type { ConflictState, HunkResolution } from "../lib/types";
import { I } from "./Icons";

interface Props {
  conflict: ConflictState;
  onResolve: (hunkIndex: number, choice: HunkResolution, customText?: string) => void;
}

const CHOICES: Array<{
  key: HunkResolution;
  label: string;
  hint: string;
  accent: string;
}> = [
  {
    key: "ours",
    label: "Keep mine",
    hint: "Use your branch's version",
    accent: "#2D9BD6",
  },
  {
    key: "theirs",
    label: "Keep theirs",
    hint: "Use the incoming branch's version",
    accent: "#7B4FB8",
  },
  {
    key: "both",
    label: "Keep both",
    hint: "Concat both — only sometimes the right call",
    accent: "#E8A93C",
  },
  {
    key: "custom",
    label: "Write custom",
    hint: "Type what you actually want",
    accent: "#2EA86A",
  },
];

export function ConflictResolver({ conflict, onResolve }: Props) {
  return (
    <div className="gf-card" style={{ display: "flex", flexDirection: "column" }}>
      <div className="gf-card__head">
        <h3>
          <span
            style={{
              display: "inline-block",
              width: 18,
              height: 18,
              borderRadius: 999,
              background: "var(--danger)",
              color: "#fff",
              textAlign: "center",
              lineHeight: "18px",
              fontWeight: 700,
              fontSize: 12,
              marginRight: 8,
              verticalAlign: -3,
            }}
          >
            !
          </span>
          Conflict in {conflict.file}
        </h3>
        <div className="gf-card__head-meta">
          {conflict.hunks.filter((h) => h.resolution).length} of{" "}
          {conflict.hunks.length} hunks resolved
        </div>
      </div>
      <div
        style={{
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 14,
          maxHeight: 520,
          overflowY: "auto",
        }}
      >
        {conflict.hunks.map((_, i) => (
          <HunkCard
            key={i}
            conflict={conflict}
            hunkIndex={i}
            onResolve={onResolve}
          />
        ))}
      </div>
    </div>
  );
}

interface HunkCardProps {
  conflict: ConflictState;
  hunkIndex: number;
  onResolve: (hunkIndex: number, choice: HunkResolution, customText?: string) => void;
}

function HunkCard({ conflict, hunkIndex, onResolve }: HunkCardProps) {
  const hunk = conflict.hunks[hunkIndex];
  const [custom, setCustom] = useState(hunk.customText ?? "");
  const resolved = hunk.resolution !== null;

  return (
    <div
      style={{
        border: `1px solid ${resolved ? "var(--success)" : "var(--border-1)"}`,
        background: resolved ? "var(--success-bg)" : "var(--bg-1)",
        borderRadius: 10,
        padding: 12,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: resolved ? "#1F6F45" : "var(--fg-3)",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          marginBottom: 10,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>Hunk {hunkIndex + 1}</span>
        {resolved && (
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <I.Check size={12} /> resolved → {hunk.resolution}
          </span>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <DiffPane
          label={`OURS (${hunk.oursLabel})`}
          text={hunk.ours}
          tone="#2D9BD6"
        />
        <DiffPane
          label={`THEIRS (${hunk.theirsLabel})`}
          text={hunk.theirs}
          tone="#7B4FB8"
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 6,
        }}
      >
        {CHOICES.map((c) => {
          const active = hunk.resolution === c.key;
          return (
            <button
              key={c.key}
              onClick={() => onResolve(hunkIndex, c.key, c.key === "custom" ? custom : undefined)}
              title={c.hint}
              style={{
                padding: "8px 6px",
                borderRadius: 8,
                border: `1px solid ${active ? c.accent : "var(--border-1)"}`,
                background: active ? c.accent : "var(--bg-1)",
                color: active ? "#fff" : "var(--fg-1)",
                fontSize: 11.5,
                fontWeight: 600,
                cursor: "pointer",
                lineHeight: 1.3,
              }}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {hunk.resolution === "custom" && (
        <textarea
          value={custom}
          onChange={(e) => {
            setCustom(e.target.value);
            onResolve(hunkIndex, "custom", e.target.value);
          }}
          spellCheck={false}
          placeholder="Write the final version here…"
          style={{
            width: "100%",
            marginTop: 10,
            minHeight: 70,
            padding: 10,
            border: "1px solid var(--border-1)",
            borderRadius: 8,
            fontFamily: "ui-monospace, Menlo",
            fontSize: 12,
            lineHeight: 1.5,
            resize: "vertical",
            background: "var(--bg-1)",
            color: "var(--fg-1)",
          }}
        />
      )}
    </div>
  );
}

function DiffPane({ label, text, tone }: { label: string; text: string; tone: string }) {
  return (
    <div
      style={{
        border: `1px solid ${tone}`,
        background: "var(--bg-2)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: tone,
          padding: "5px 8px",
          borderBottom: `1px solid ${tone}`,
          background: "var(--bg-1)",
        }}
      >
        {label}
      </div>
      <pre
        style={{
          margin: 0,
          padding: "8px 10px",
          fontFamily: "ui-monospace, Menlo",
          fontSize: 11.5,
          lineHeight: 1.55,
          color: "var(--fg-1)",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {text}
      </pre>
    </div>
  );
}
